// 游线（route）= 一条完整的两坝一峡游览线路；每条线分上行 / 下行两个方向。
// 点位（scene）仍然存在 scenes mock 中，game/route 层只是"组织 + 排序 + 方向化讲解词"。
// 一个 scene 可被多条 route 引用；在同一 route 的不同 direction 下，讲解词通常不同。

export type Direction = "up" | "down";

export interface Route {
  id: string;
  name: string;
  shortName: string;
  description: string;
  coverUrl?: string;
  /** display order in the list */
  seq: number;
  /** draft = 保存（草稿，未对游客可见）; active = 已发布; offline = 已下线 */
  status: "active" | "draft" | "offline";
  /** estimated total duration label, e.g. "约 4 小时" */
  durationLabel: string;
  /** total number of distinct scenes (across both directions union) */
  sceneCount: number;
  /** whether this route has up / down direction points */
  hasUp?: boolean;
  hasDown?: boolean;
}

export interface RoutePoint {
  routeId: string;
  direction: Direction;
  /** order within this direction */
  seq: number;
  sceneId: string;
  /** direction-specific narration. Empty string = fall back to scene default. */
  script: string;
  voice?: string;
  /** seconds; 0 = no pause between this and the next point */
  pauseAfterSec: number;
  /** generation status of this direction-specific audio */
  audioStatus: "generated" | "pending" | "none";
}

export const routes: Route[] = [
  {
    id: "r1",
    name: "两坝一峡经典航线 A",
    shortName: "经典A线",
    description: "葛洲坝 → 西陵峡 → 三峡大坝 全程游船经典航线，覆盖核心点位 8 处。",
    seq: 1,
    status: "active",
    durationLabel: "约 4 小时",
    sceneCount: 8,
    hasUp: true,
    hasDown: true,
  },
  {
    id: "r2",
    name: "两坝一峡深度航线 B",
    shortName: "深度B线",
    description: "增加屈原故里、白帝城、神女峰文化人文点位，全长约 6 小时，适合深度游。",
    seq: 2,
    status: "active",
    durationLabel: "约 6 小时",
    sceneCount: 12,
    hasUp: true,
    hasDown: true,
  },
  {
    id: "r3",
    name: "夜游短线",
    shortName: "夜游短线",
    description: "三峡大坝灯光夜游短线，1.5 小时，仅覆盖坝区 4 个点位。",
    seq: 3,
    status: "draft",
    durationLabel: "约 1.5 小时",
    sceneCount: 4,
    hasUp: true,
    hasDown: false,
  },
];

// Route A 上行（葛洲坝→西陵峡→三峡大坝）
const r1_up: RoutePoint[] = [
  { routeId: "r1", direction: "up", seq: 1, sceneId: "3",  script: "（上行）我们从葛洲坝出发，作为长江上第一座大型水利枢纽……", voice: "鲟侠船长", pauseAfterSec: 3, audioStatus: "generated" },
  { routeId: "r1", direction: "up", seq: 2, sceneId: "7",  script: "（上行）即将穿过南津关，这里是西陵峡的东大门……",        voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "generated" },
  { routeId: "r1", direction: "up", seq: 3, sceneId: "1",  script: "（上行）我们正在驶入西陵峡，全长 66 公里，是三峡最长的一段……", voice: "鲟侠船长", pauseAfterSec: 4, audioStatus: "generated" },
  { routeId: "r1", direction: "up", seq: 4, sceneId: "6",  script: "（上行）左舷三游洞，唐代白居易等三人同游而得名……",     voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "pending"   },
  { routeId: "r1", direction: "up", seq: 5, sceneId: "4",  script: "（上行）前方的黄牛岩，相传大禹治水时神牛助其开山……",   voice: "鲟侠船长", pauseAfterSec: 3, audioStatus: "generated" },
  { routeId: "r1", direction: "up", seq: 6, sceneId: "5",  script: "（上行）石牌要塞，二战石牌保卫战的主战场……",          voice: "金梅导游", pauseAfterSec: 3, audioStatus: "generated" },
  { routeId: "r1", direction: "up", seq: 7, sceneId: "10", script: "（上行）太平溪，三峡大坝上游的支流……",                 voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "none"      },
  { routeId: "r1", direction: "up", seq: 8, sceneId: "2",  script: "（上行）前方就是三峡大坝主体，世界上最大的水利枢纽……", voice: "鲟侠船长", pauseAfterSec: 4, audioStatus: "generated" },
];
// Route A 下行（三峡大坝→西陵峡→葛洲坝） —— 顺序相反、讲解词不同
const r1_down: RoutePoint[] = [
  { routeId: "r1", direction: "down", seq: 1, sceneId: "2",  script: "（下行）刚刚我们从坝顶眺望，现在正穿过三峡大坝五级船闸……", voice: "鲟侠船长", pauseAfterSec: 4, audioStatus: "generated" },
  { routeId: "r1", direction: "down", seq: 2, sceneId: "10", script: "（下行）太平溪在右舷，回望坝区灯火……",                     voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "none"      },
  { routeId: "r1", direction: "down", seq: 3, sceneId: "5",  script: "（下行）石牌要塞迎面而来，逆光下更能体会当年战事的险峻……", voice: "金梅导游", pauseAfterSec: 3, audioStatus: "generated" },
  { routeId: "r1", direction: "down", seq: 4, sceneId: "4",  script: "（下行）黄牛岩从右舷过……",                                 voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "generated" },
  { routeId: "r1", direction: "down", seq: 5, sceneId: "6",  script: "（下行）回望三游洞，唐代石刻历历可见……",                  voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "pending"   },
  { routeId: "r1", direction: "down", seq: 6, sceneId: "1",  script: "（下行）即将驶出西陵峡，请大家最后再看一眼两岸石壁……",   voice: "鲟侠船长", pauseAfterSec: 3, audioStatus: "generated" },
  { routeId: "r1", direction: "down", seq: 7, sceneId: "7",  script: "（下行）南津关在前方，西陵峡正式结束……",                  voice: "鲟侠船长", pauseAfterSec: 2, audioStatus: "generated" },
  { routeId: "r1", direction: "down", seq: 8, sceneId: "3",  script: "（下行）抵达葛洲坝，本次航程即将结束……",                  voice: "鲟侠船长", pauseAfterSec: 0, audioStatus: "generated" },
];

// Route B 上行（深度，多 4 个人文点位）
const r2_up: RoutePoint[] = [
  ...r1_up.slice(0, 1),
  { routeId: "r2", direction: "up", seq: 2, sceneId: "9",  script: "（上行）秭归港，三峡库区重要的航运枢纽……",                voice: "金梅导游", pauseAfterSec: 2, audioStatus: "none"      },
  { routeId: "r2", direction: "up", seq: 3, sceneId: "8",  script: "（上行）屈原故里位于秭归，有屈原祠与屈原墓等遗迹……",     voice: "鲟侠船长", pauseAfterSec: 4, audioStatus: "generated" },
  ...r1_up.slice(1).map((p, i) => ({ ...p, routeId: "r2", seq: i + 4 })),
  { routeId: "r2", direction: "up", seq: 12, sceneId: "11", script: "（上行）尾段额外深入到白帝城，刘备托孤之地……",            voice: "金梅导游", pauseAfterSec: 3, audioStatus: "generated" },
];
const r2_down: RoutePoint[] = r2_up.slice().reverse().map((p, i) => ({
  ...p,
  direction: "down" as Direction,
  seq: i + 1,
  script: p.script.replace("（上行）", "（下行）"),
}));

// Route C（夜游短线）只在大坝区域，仅有上行
const r3_up: RoutePoint[] = [
  { routeId: "r3", direction: "up", seq: 1, sceneId: "10", script: "夜幕降临，我们从太平溪出发……",      voice: "金梅导游", pauseAfterSec: 2, audioStatus: "none"    },
  { routeId: "r3", direction: "up", seq: 2, sceneId: "2",  script: "三峡大坝灯光秀正前方点亮……",        voice: "金梅导游", pauseAfterSec: 4, audioStatus: "pending" },
  { routeId: "r3", direction: "up", seq: 3, sceneId: "5",  script: "夜色中的石牌要塞别有韵味……",        voice: "金梅导游", pauseAfterSec: 2, audioStatus: "none"    },
  { routeId: "r3", direction: "up", seq: 4, sceneId: "1",  script: "西陵峡口短暂停留，本次夜游结束。", voice: "金梅导游", pauseAfterSec: 0, audioStatus: "none"    },
];

export const routePoints: RoutePoint[] = [
  ...r1_up, ...r1_down,
  ...r2_up, ...r2_down,
  ...r3_up,
];

export const routeStats = {
  totalRoutes: routes.length,
  activeRoutes: routes.filter((r) => r.status === "active").length,
  totalDirections: routes.length * 2 - 1, // r3 只有上行
  pointsCount: routePoints.length,
};

export function pointsOf(routeId: string, direction: Direction): RoutePoint[] {
  return routePoints
    .filter((p) => p.routeId === routeId && p.direction === direction)
    .sort((a, b) => a.seq - b.seq);
}

export function hasDirection(routeId: string, direction: Direction): boolean {
  return routePoints.some((p) => p.routeId === routeId && p.direction === direction);
}
